import React from "react";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaModalIconWrapper from "../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaH5 from "../../ui-kit/typography/H5/H5";
import KuikaText from "../../ui-kit/typography/Text/Text";
import "./branching-modal.scss";

export interface IActivateBranchingModalProps {
  showModal: boolean;
  inProgress?: boolean;
  /**
   * Where the commit stands (`KuikaButton` `status`). Given, the commit button carries it —
   * the busy word, then its ✓ or "Try again" — and `inProgress` no longer draws on it.
   */
  status?: KuikaButtonStatus;
  onClose: () => void;
  onActivate: () => void;
}

const ACTIVATE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Activating", success: "Activated" };

/**
 * Confirmation gate in front of Version Control activation (the header's activate chip opens
 * this). Activation is cheap to run but heavy to walk back — deactivation deletes branches and
 * history — so the switch-on deserves the same deliberate modal the switch-off has
 * (DeactivateBranchingModal, whose anatomy this mirrors).
 */
const ActivateBranchingModal: React.FC<IActivateBranchingModalProps> = ({
  showModal,
  inProgress,
  status,
  onClose,
  onActivate
}) => {
  const footer = (
    <KModalFooter
      closesOnSuccess
      onCancel={onClose}
      submitLabel="Activate"
      submitTestId="activateBranchingConfirmButton"
      onSubmit={onActivate}
      loading={inProgress}
      status={status}
      statusLabels={ACTIVATE_STATUS_LABELS}
    />
  );

  return (
    <KModal
      showModal={showModal}
      closeModal={onClose}
      header="Activate Version Control"
      footer={footer}
      width={420}
      centered
    >
      <div className="kuika-branching-modal">
        <div className="kuika-branching-modal__header">
          <KuikaModalIconWrapper iconName="git-branch" intent="create" />
          <div className="kuika-branching-modal__title">
            <KuikaH5>Activate Version Control</KuikaH5>
          </div>
        </div>
        <div className="kuika-branching-modal__content">
          {/* Design's benefit line leads (the "why"); the bullets keep the concrete mechanics and
              the deactivation warning the short version dropped. "Creates your first branch from
              the current design" is folded into the lead-in so it isn't repeated as a bullet. */}
          <div className="kuika-branching-modal__description">
            <KuikaText>
              Track every change, branch off to experiment safely, and merge back when you&apos;re ready.
            </KuikaText>
            <KuikaText>
              Activating creates <strong>master</strong>, your first branch, from the current design:
            </KuikaText>
          </div>
          <ul className="kuika-branching-modal__list">
            <li>
              From then on, changes are staged and saved as <strong>commits</strong>
            </li>
            <li>
              Work in parallel on <strong>branches</strong> and combine it with <strong>merge requests</strong>
            </li>
            <li>
              Deactivating later keeps only the master branch — branches and history are <strong>deleted</strong>
            </li>
          </ul>
        </div>
      </div>
    </KModal>
  );
};

export default ActivateBranchingModal;
